import { INCORRECT_MASTER_KEY } from "./cryptoErrors";

const BATCH_SIZE = 50;
const cryptoError = (message, cause, details = {}) => Object.assign(new Error(message), { cause, ...details });
const mapInBatches = async (items, mapper) => {
  const result = [];
  for (let index = 0; index < items.length; index += BATCH_SIZE)
    result.push(...(await Promise.all(items.slice(index, index + BATCH_SIZE).map(mapper))));
  return result;
};

export class CryptoTransitionCoordinator {
  constructor({ loadEmployees, transitionService, verifierApi, createTargetEngine, activateTarget, assertSession = () => {} }) {
    this.loadEmployees = loadEmployees;
    this.transitionService = transitionService;
    this.verifierApi = verifierApi;
    this.createTargetEngine = createTargetEngine;
    this.activateTarget = activateTarget;
    this.assertSession = assertSession;
  }

  async prepare(sourceEngine, targetEngine) {
    let sourceCompanyHash;
    let targetCompanyHash;
    let employees;
    try {
      sourceCompanyHash = await sourceEngine.getCompanyHash();
      targetCompanyHash = await targetEngine.getCompanyHash();
      if (!sourceCompanyHash || !targetCompanyHash) throw new Error("Crypto transition is missing a company hash");
      employees = await this.loadEmployees(sourceCompanyHash);
      if (!Array.isArray(employees)) throw new Error("Crypto transition employee loading returned an invalid response");
    } catch (error) {
      throw cryptoError("Crypto transition preparation failed before persistence", error, {
        failureStage: "prepare",
        safeToContinue: true,
        requiresRecovery: false,
        persistenceState: "source",
      });
    }
    const ids = new Set();
    const oldEmployeesHashes = employees.map((employee) => {
      const employeeId = employee?.employeeId ?? employee?.id ?? employee?.employee?.employeeId ?? employee?.employee?.id;
      if (employeeId == null || !employee?.hash)
        throw cryptoError("Crypto transition found an invalid employee record", null, {
          failureStage: "transform-input",
          safeToContinue: true,
          requiresRecovery: false,
          persistenceState: "source",
        });
      const normalizedId = String(employeeId);
      if (ids.has(normalizedId))
        throw cryptoError(`Crypto transition found duplicate employeeId ${employeeId}`, null, {
          failureStage: "transform-input",
          safeToContinue: true,
          requiresRecovery: false,
          persistenceState: "source",
        });
      ids.add(normalizedId);
      return { employeeId, hash: employee.hash };
    });

    let newEmployeesHashes;
    try {
      newEmployeesHashes = await mapInBatches(employees, async (employee) => {
        const employeeId = employee?.employeeId ?? employee?.id ?? employee?.employee?.employeeId ?? employee?.employee?.id;
        const nameOrNum = await sourceEngine.decryptEmployee(employee.hash);
        if (!nameOrNum || nameOrNum === INCORRECT_MASTER_KEY) throw new Error(`Cannot decrypt employee hash (employeeId=${employeeId})`);
        return { employeeId, hash: await targetEngine.encryptEmployee(nameOrNum) };
      });
    } catch (error) {
      throw cryptoError("Employee crypto transition failed before persistence", error, {
        failureStage: "transform",
        safeToContinue: true,
        requiresRecovery: false,
        persistenceState: "source",
      });
    }

    return {
      oldMasterKey: { companyHash: sourceCompanyHash, employeesHashes: oldEmployeesHashes },
      newMasterKey: { companyHash: targetCompanyHash, employeesHashes: newEmployeesHashes },
    };
  }

  async commit({ sourceEngine, targetMasterKey, expectedRevision }) {
    let targetEngine;
    let targetActivated = false;
    try {
      targetEngine = await this.createTargetEngine(targetMasterKey);
    } catch (error) {
      throw cryptoError("Target crypto engine initialization failed before persistence", error, {
        failureStage: "target-engine",
        safeToContinue: true,
        requiresRecovery: false,
        persistenceState: "source",
      });
    }
    try {
      const sourceVersion = sourceEngine.version;
      const assertSourceSession = () => {
        try {
          this.assertSession(expectedRevision);
        } catch (error) {
          throw cryptoError("Crypto session changed before the remote transition started", error, {
            failureStage: "pre-persistence-session",
            safeToContinue: true,
            requiresRecovery: false,
            persistenceState: "source",
          });
        }
      };
      assertSourceSession();
      const payload = await this.prepare(sourceEngine, targetEngine);
      assertSourceSession();
      const transition = await this.transitionService.apply({ payload });
      let verifierResponse;
      try {
        verifierResponse = await this.verifierApi.changeHash(await targetEngine.getValidationHash());
      } catch (error) {
        const verifierState = error?.requestSent === false ? "source" : "unknown";
        throw cryptoError("Survey data was transitioned, but the verifier update failed", error, {
          failureStage: "verifier-update",
          persistenceState: `survey-target-verifier-${verifierState}`,
          requiresRecovery: true,
          safeToContinue: false,
        });
      }
      try {
        this.activateTarget(targetEngine, expectedRevision);
        targetActivated = true;
      } catch (error) {
        throw cryptoError("The remote crypto transition completed, but the local target session could not be activated", error, {
          failureStage: "local-activation",
          persistenceState: "target",
          requiresRecovery: true,
          safeToContinue: false,
        });
      }
      try {
        sourceEngine.purge();
      } catch (error) {
        console.error("Crypto transition could not purge the detached source engine", error);
      }
      return {
        payload,
        transition,
        verifierResponse,
        sourceVersion,
        targetVersion: targetEngine.version,
      };
    } catch (error) {
      if (!targetActivated) {
        try {
          targetEngine.purge();
        } catch (cleanupError) {
          console.error("Crypto transition could not purge the unused target engine", cleanupError);
        }
      }
      throw error;
    }
  }
}
